import { Autocomplete, Box, CircularProgress, TextField, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material'
import React, { type SyntheticEvent, useEffect, useRef, useState } from 'react'
import { type State, searchStatesByCountry, addState, type Country } from 'services'
import { useCountriesLuts, useUsCountriesLuts, useGlobalMessageHandler } from 'components'
import { SessionRegion, useSessionContext, AccessLevel } from 'session'
import { formatErrorFieldMessages } from 'interfaces'

interface StateSelectProps {
  country: Country | null
  state: State | null
  handleStateChange: (state: State | null) => void
  disabled?: boolean
}

export const StateSelect: React.FC<StateSelectProps> = ({ country, state, handleStateChange, disabled = false }) => {
  const [states, setStates] = useState<State[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [creatingState, setCreatingState] = useState<boolean>(false)
  const [filteredOptions, setFilteredOptions] = useState<State[]>([])
  const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false)
  const [pendingStateToCreate, setPendingStateToCreate] = useState<State | null>(null)
  const { sessionData } = useSessionContext()
  const { lutValues: countriesLutValues, setLutValues: setCountriesLutValues } = sessionData.region === SessionRegion.INTL ? useCountriesLuts() : useUsCountriesLuts()
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()
  const { hasAccessLevel } = useSessionContext()
  const timeoutRef = useRef<NodeJS.Timeout | undefined>()

  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    setInputValue(state?.name ?? '')
  }, [state])

  useEffect(() => {
    if (country == null) {
      setStates([])
      setFilteredOptions([])
      return
    }

    if (country.states?.length > 0) {
      setStates(country.states)
      setFilteredOptions(country.states)
      return
    } else {
      const foundCountry = countriesLutValues.countries.find(lutCountry => lutCountry.id === country.id)
      if (foundCountry != null) {
        setStates(foundCountry.states)
        setFilteredOptions(foundCountry.states)
        return
      }
    }

    const fetchStates = async (): Promise<void> => {
      try {
        setLoading(true)
        const statesData = await searchStatesByCountry(country.id)
        if (statesData.find(value => value.id === state?.id) == null) {
          handleStateChange(null)
        }
        setStates(statesData)
        setFilteredOptions(statesData)
      } catch (error) {
        console.error('Error fetching states:', error)
        setStates([])
        setFilteredOptions([])
        handleStateChange(null)
      } finally {
        setLoading(false)
      }
    }

    if (!disabled) { void fetchStates() }
  }, [country?.id, disabled])

  const filterStates = (input: string): State[] => {
    const normalizedInput = input.toLowerCase()
    return states.filter(state =>
      state.name.toLowerCase().includes(normalizedInput)
    )
  }

  const onInputChange = (event: React.SyntheticEvent, newInputValue: string): void => {
    setInputValue(newInputValue)

    if (event?.type === 'change') {
      handleStateChange(null)
    }

    if (newInputValue.length < 2) {
      setFilteredOptions(states)
      return
    }

    clearTimeout(timeoutRef.current)

    const timeoutId = setTimeout(() => {
      const filtered = filterStates(newInputValue)
      const trimmedInput = newInputValue.trim()

      const hasExactMatch = states.some(state =>
        state.name.toLowerCase() === trimmedInput.toLowerCase()
      )

      const newStateOption: State = {
        id: -1,
        name: trimmedInput,
        countryId: country?.id
      }

      const optionsWithAdd = hasAccessLevel(AccessLevel.EDITOR) &&
                            !hasExactMatch &&
                            trimmedInput.length >= 2 &&
                            trimmedInput.length <= 100
        ? [newStateOption, ...filtered]
        : filtered

      if (timeoutRef.current === timeoutId) {
        setFilteredOptions(optionsWithAdd)
      }
    }, 150)

    timeoutRef.current = timeoutId
  }

  const handleStateSelection = (
    _event: SyntheticEvent<Element, Event>,
    value: State | null
  ): void => {
    if (value != null && value.id === -1) {
      setPendingStateToCreate(value)
      setConfirmationOpen(true)
    } else {
      handleStateChange(value)
    }
  }

  const handleConfirmStateCreation = (): void => {
    if (pendingStateToCreate == null) return

    setConfirmationOpen(false)
    setCreatingState(true)
    clearMessages()

    addState(pendingStateToCreate).then((createdState) => {
      const newState: State = {
        id: createdState.id,
        name: pendingStateToCreate.name,
        countryId: country?.id
      }

      setStates([...states, newState])
      setFilteredOptions([])

      if (country?.id != null) {
        const updatedCountries = countriesLutValues.countries.map(c => {
          if (c.id === country.id) {
            return {
              ...c,
              states: [...(c.states ?? []), newState]
            }
          }
          return c
        })

        setCountriesLutValues({
          ...countriesLutValues,
          countries: updatedCountries
        })
      }

      handleStateChange(newState)
      showSuccessMessage('State/Province added successfully')
    }).catch((error) => {
      showErrorMessage(formatErrorFieldMessages(error.response.data.errors))
    }).finally(() => {
      setCreatingState(false)
      setPendingStateToCreate(null)
    })
  }

  const handleCancelStateCreation = (): void => {
    setConfirmationOpen(false)
    setPendingStateToCreate(null)
  }

  if (loading || creatingState) {
    return <CircularProgress/>
  }

  const stateOptions = filteredOptions.length > 0 ? filteredOptions : states

  return (
    <>
    <Autocomplete
      id="state-address-select"
      disabled={disabled}
      options={stateOptions}
      isOptionEqualToValue={(option, value) => {
        if (option == null || value == null) return false
        return option.id === value.id
      }}
      getOptionLabel={(option) => {
        if (option == null) return ''
        return option.id === -1 ? `Add "${option.name}"` : (option.name ?? '')
      }}
      renderOption={(props, option) => (
        <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
          {option.id === -1 ? `Add "${option.name}"` : (option.name ?? '')}
        </Box>
      )}
      value={state}
      onChange={handleStateSelection}
      inputValue={inputValue}
      onInputChange={onInputChange}
      onBlur={() => { setFilteredOptions([]) }}
      autoHighlight
      renderInput={(params) => (
        <TextField
          {...params}
          label="State/province"
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password' // disable autocomplete and autofill
          }}
        />
      )}
    />

    <Dialog
      open={confirmationOpen}
      onClose={handleCancelStateCreation}
      aria-labelledby="confirm-state-creation-title"
      aria-describedby="confirm-state-creation-description"
    >
      <DialogTitle id="confirm-state-creation-title">
        Create New State/Province
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-state-creation-description">
          Are you sure you want to create a new state/province &ldquo;{pendingStateToCreate?.name ?? ''}&rdquo;
          for {countriesLutValues.countries.find(c => c.id === country?.id)?.country_desc ?? 'this country'}?

          This will add it to the database and make it available for all users.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelStateCreation} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleConfirmStateCreation}
          color="primary"
          variant="contained"
          disabled={creatingState}
        >
          {creatingState ? 'Creating...' : 'Create State/Province'}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  )
}
